import { useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "../axiosClient/userApi";
import { zodParseWithLog } from "../apiErrorLogs";
import { z } from "zod";
import { CompanySectorZod } from "src/api/client-api/companySectors/useSupplierSectors.tsx";
import { isQueryReady } from "../axiosClient/utils";

export const userMeKey = "usersMe" as const;

export const useUserNew = () => {
    const isEnabled = true;

    const query = useQuery({
        queryKey: [userMeKey],
        enabled: isEnabled,
        staleTime: 1000 * 60 * 5,
        queryFn: async () => {
            const url = "/api/users/me";

            return await loggedInApiCall.get(url);
        },
        select: (request) => {
            const user = zodParseWithLog(request.data, UserZod, userMeKey);
            return user;
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        user: query.data,
        isReady,
        ...query,
    };
};

type UpdateCurrentUserProfileInput = {
    firstname: string;
    lastname: string;
};

type UpdateCurrentUserPasswordInput = {
    plainPassword: string;
};

const patchCurrentUser = async (data: Partial<Pick<User, "firstname" | "lastname">> & { plainPassword?: string }) => {
    const response = await loggedInApiCall.patch("/api/users/me", data, {
        headers: {
            "Content-Type": "application/merge-patch+json",
        },
    });

    return zodParseWithLog(response.data, UserZod, userMeKey);
};

export const useUpdateCurrentUserProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ firstname, lastname }: UpdateCurrentUserProfileInput) =>
            patchCurrentUser({ firstname, lastname }),
        onSuccess: async () => {
            await queryClient.invalidateQueries([userMeKey]);
        },
    });
};

export const useUpdateCurrentUserPassword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ plainPassword }: UpdateCurrentUserPasswordInput) => patchCurrentUser({ plainPassword }),
        onSuccess: async () => {
            await queryClient.invalidateQueries([userMeKey]);
        },
    });
};

const OrganizationZod = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    organizationSector: CompanySectorZod.optional(),
    isProvider: z.boolean(),
    permissions: z.array(z.string()),
});

export type Company = z.infer<typeof OrganizationZod>;

export const UserZod = z
    .object({
        email: z.string(),
        id: z.number(),
        is_verified: z.boolean(),
        roles: z.array(z.string()),
        organizations: OrganizationZod.array(),
        lastname: z.string(),
        firstname: z.string(),
        permissions: z.array(z.tuple([z.string(), z.string()])),
        socialLastSeenAt: z.string().nullable().optional(),
        hasOnlyArchivedOrganizations: z.boolean().optional(),
    })
    .readonly();

export type User = z.infer<typeof UserZod>;

export type CurrentUser = User;
